import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Drawer, Grid, Popover, List, Typography, Space, Tooltip } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import notificationService, { UNREAD_EVENT, emitUnread } from '../services/notificationService';
import { LEVEL_COLOR, openLink } from '../pages/account/NotificationInbox';
import { useTranslation } from '../../shared/i18n';
import { formatDateTime } from '../../shared/utils/date';
import { userPath } from '../../shared/config/paths';

const { Text } = Typography;

/** 顶栏铃铛：未读数（60s 轮询）+ 最近 6 条 */
export default function NotificationBell() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState(null);
    // phones: the header has more buttons right of the bell, so a 320px popover anchored to it runs off-screen
    const mobile = !Grid.useBreakpoint().sm;

    useEffect(() => {
        const load = () => { if (!document.hidden) notificationService.unreadCount().then((d) => setUnread(d?.unread || 0)).catch(() => {}); };
        load();
        const timer = setInterval(load, 60000);
        const onEvt = (e) => setUnread(Number(e.detail) || 0);
        window.addEventListener(UNREAD_EVENT, onEvt);
        return () => { clearInterval(timer); window.removeEventListener(UNREAD_EVENT, onEvt); };
    }, []);

    const loadItems = useCallback(() => {
        notificationService.inbox({ page: 1, page_size: 6 }).then((d) => { setItems(d.items || []); setUnread(d.unread || 0); }).catch(() => setItems([]));
    }, []);

    const onOpenChange = (v) => {
        setOpen(v);
        if (v) loadItems();
    };

    const click = (item) => {
        setOpen(false);
        if (!item.read_at) notificationService.markRead([item.id]).then((r) => emitUnread(r.unread)).catch(() => {});
        if (item.link) openLink(navigate, item.link);
        else navigate(userPath('/settings?tab=inbox'));
    };

    const content = (
        <div style={mobile ? undefined : { width: 320, maxWidth: '80vw' }}>
            <List
                size="small"
                loading={items === null}
                dataSource={items || []}
                locale={{ emptyText: t('No messages') }}
                renderItem={(item) => (
                    <List.Item style={{ cursor: 'pointer', paddingInline: 4 }} onClick={() => click(item)}>
                        <Space align="start" size={8}>
                            <Badge dot={!item.read_at} color={LEVEL_COLOR[item.level]} />
                            <div>
                                <Text strong={!item.read_at} ellipsis style={{ maxWidth: mobile ? 'calc(100vw - 96px)' : 270, display: 'block' }}>{item.title}</Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>{formatDateTime(item.created_at)}</Text>
                            </div>
                        </Space>
                    </List.Item>
                )}
            />
            <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: 6 }}>
                <Button type="link" size="small" disabled={!unread} onClick={() => notificationService.markRead(null).then((r) => { emitUnread(r.unread); loadItems(); }).catch(() => {})}>{t('Mark all read')}</Button>
                <Button type="link" size="small" onClick={() => { setOpen(false); navigate(userPath('/settings?tab=inbox')); }}>{t('View all')}</Button>
            </Space>
        </div>
    );

    const bell = (
        <Badge count={unread} size="small" overflowCount={99} offset={[-2, 2]}>
            <Button type="text" shape="circle" aria-label={t('Notifications')} icon={<BellOutlined />} onClick={mobile ? () => onOpenChange(true) : undefined} />
        </Badge>
    );
    if (mobile) {
        return (
            <>
                {bell}
                <Drawer open={open} onClose={() => setOpen(false)} placement="right" width="88vw" title={t('Notifications')}
                    styles={{ body: { padding: '8px 16px' } }}>
                    {content}
                </Drawer>
            </>
        );
    }

    return (
        <Popover content={content} trigger="click" placement="bottomRight" open={open} onOpenChange={onOpenChange} arrow={false}>
            <Tooltip title={open ? '' : t('Notifications')}>
                <Badge count={unread} size="small" overflowCount={99} offset={[-2, 2]}>
                    <Button type="text" shape="circle" aria-label={t('Notifications')} icon={<BellOutlined />} />
                </Badge>
            </Tooltip>
        </Popover>
    );
}
